import type { AuditEvent } from './api';

/**
 * Audit actions, in English.
 *
 * The stored action is a stable code (`request.approve`), because that is what
 * a filter and a query want. What a person reading the trail wants is a
 * sentence. Keeping the two apart means the label can be reworded without
 * orphaning years of history, which renaming the code would do.
 *
 * Unknown codes fall back to a readable form of themselves rather than to
 * nothing, so an action added on the server is legible here before this file
 * catches up.
 */
const LABELS: Record<string, string> = {
  'auth.signin': 'Signed in',
  'auth.signout': 'Signed out',

  'request.create': 'Started a request',
  'request.submit': 'Submitted for approval',
  'request.approve': 'Approved and signed',
  'request.reject': 'Rejected',
  'request.return': 'Returned for changes',
  'request.cancel': 'Discarded',
  'request.claim': 'Took it on',
  'request.release': 'Put it back in the pool',
  'request.assign': 'Assigned',
  'request.complete': 'Implemented and signed off',
  'request.nudge': 'Sent a reminder',
  'request.link_enrolment': 'Linked to an enrolment',
  'request.amend': 'Amended by an approver',
  'request.delete': 'Deleted the record permanently',
  'request.clone': 'Cloned from a previous request',
  'request.onsite_push': 'Pushed to the onsite tech',
  'request.onsite_done': 'Ticked the onsite work done',

  'admin.user.update': 'Changed a user',
  'admin.user.walkthrough_reset': 'Reset an introduction',
  'admin.user.delete': 'Deleted a user account',
  'admin.catalog.create': 'Added a form item',
  'admin.catalog.update': 'Edited a form item',
  'admin.catalog.delete': 'Deleted a form item',
  'admin.department.create': 'Added a department',
  'admin.department.update': 'Edited a department',
  'admin.department.delete': 'Deleted a department',
  'admin.settings.update': 'Changed system settings',

  'archive.export': 'Exported the archive',
  'archive.export_documents': 'Downloaded signed forms',
  'audit.export': 'Exported the audit log',
};

export function actionLabel(action: string): string {
  const known = LABELS[action];
  if (known) return known;
  const tail = action.split('.').slice(1).join(' ').replace(/_/g, ' ');
  if (!tail) return action;
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}

/** The coarse grouping the filter offers, taken from the code's first part. */
export function actionGroup(action: string): string {
  return action.split('.')[0] ?? action;
}

const GROUP_LABELS: Record<string, string> = {
  request: 'Requests',
  admin: 'Administration',
  auth: 'Sign in and out',
  archive: 'Exports',
};

export function groupLabel(group: string): string {
  return GROUP_LABELS[group] ?? group.charAt(0).toUpperCase() + group.slice(1);
}

/**
 * What changed, as a list of field-level differences.
 *
 * Only fields that actually moved: a `before`/`after` pair often carries
 * context that did not change, and printing all of it buries the one thing the
 * reader came for.
 */
export type Change = { field: string; from: unknown; to: unknown };

export function changesOf(event: AuditEvent): Change[] {
  const before = (event.before ?? {}) as Record<string, unknown>;
  const after = (event.after ?? {}) as Record<string, unknown>;
  if (typeof before !== 'object' || typeof after !== 'object') return [];

  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return keys
    .map((field) => ({ field, from: before[field], to: after[field] }))
    .filter((change) => JSON.stringify(change.from) !== JSON.stringify(change.to));
}

const FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  role: 'Role',
  isActive: 'Active',
  selfApprove: 'Self-approval',
  departmentId: 'Department',
  nudgeIntervalHours: 'Reminder interval (hours)',
  retentionMonths: 'Retention (months)',
  mailFromName: 'Sender name',
  mailReplyTo: 'Reply-to',
  pdfHeaderText: 'PDF header',
  pdfFooterText: 'PDF footer',
  onsiteTechEmail: 'Onsite tech mailbox',
  to: 'Sent to',
  note: 'Note',
  reference: 'Reference',
  employeeName: 'Employee',
  requestedBy: 'Raised by',
  itemCount: 'Items',
  signaturesDestroyed: 'Signatures destroyed',
  auditEventsDetached: 'Audit events left without an actor',
  email: 'Email',
  displayName: 'Name',
  department: 'Department',
  lastLoginAt: 'Last sign-in',
  createdAt: 'Created',
  deleted: 'Deleted',
  reason: 'Reason',
  type: 'Type',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Enum values people already know by another name.
 *
 * The trail stores the enum, which is right: it is what the database holds.
 * But "DRAFT → CANCELLED" in an activity feed is the machine's vocabulary in a
 * place where the reader has been told "Discarded" everywhere else, and two
 * names for one thing is how somebody comes to think they are two things.
 */
const VALUE_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Awaiting approval',
  CHANGES_REQUESTED: 'Changes requested',
  APPROVED: 'Approved',
  IN_PROGRESS: 'With IT',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',

  ENROLMENT: 'Enrolment',
  OFFBOARDING: 'Offboarding',

  PENDING: 'Pending access',
  REQUESTER: 'Requester',
  APPROVER: 'Approver',
  ADMIN: 'Administrator',
  SUPER_ADMIN: 'Super Admin',
  SYSTEM_ADMIN: 'System administrator',
  MTN_TECHNICIAN: 'MTN Technician',
  APPROVED_VIEWER: 'Approved requests viewer',

  FIXED_TERM: 'Fixed term',
  PERMANENT: 'Permanent',
};

export function valueLabel(value: unknown): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return VALUE_LABELS[value] ?? value;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * What an event was about, in English.
 *
 * The stored `entityType` is a Prisma model name, which is the right thing to
 * store and the wrong thing to print: "AppSettings" in a column headed "On"
 * tells a reader nothing they came for.
 */
const ENTITY_LABELS: Record<string, string> = {
  Request: 'a request',
  User: 'a user account',
  CatalogItem: 'a form item',
  Department: 'a department',
  AppSettings: 'system settings',
  Session: 'a sign-in',
};

export function entityLabel(entityType: string): string {
  return ENTITY_LABELS[entityType] ?? entityType;
}
